import React, { useEffect, useState } from 'react'
import { useNotifications } from '../../hooks/useNotifications'

interface MeasurementPointModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (properties: any) => void
  coordinates: [number, number]
  targetSegment: any // opcjonalny - punkt może być samodzielny
  editPoint?: any    // gdy podany - tryb edycji istniejącego punktu
}

interface ControllerOption {
  id: number
  identifier: string
  name: string | null
}

const MEASUREMENT_TYPES = [
  { value: 'FLOW_METER', label: 'Przepływomierz' },
  { value: 'PRESSURE_SENSOR', label: 'Czujnik ciśnienia' },
  { value: 'COMBINED_SENSOR', label: 'Czujnik kombinowany' },
  { value: 'LEVEL_SENSOR', label: 'Czujnik poziomu' },
]

export default function MeasurementPointModal({
  isOpen,
  onClose,
  onSave,
  coordinates,
  targetSegment,
  editPoint,
}: MeasurementPointModalProps) {
  const { showError, showSuccess } = useNotifications()
  const isEdit = !!editPoint

  const [name, setName] = useState('')
  const [measurementType, setMeasurementType] = useState('FLOW_METER')
  const [meterSerial, setMeterSerial] = useState('')
  const [controllerId, setControllerId] = useState<string>('')
  const [channel, setChannel] = useState<string>('')
  const [controllers, setControllers] = useState<ControllerOption[]>([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    // Pre-fill w trybie edycji, inaczej wyczyść
    setName(editPoint?.name ?? '')
    setMeasurementType(editPoint?.measurement_type ?? 'FLOW_METER')
    setMeterSerial(editPoint?.meter_serial ?? '')
    setControllerId(editPoint?.controller_id != null ? String(editPoint.controller_id) : '')
    setChannel(editPoint?.channel != null ? String(editPoint.channel) : '')
    // Pobierz listę kontrolerów do przypięcia
    const token = localStorage.getItem('token')
    fetch('/api/telemetry/controllers', { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : []))
      .then((data: ControllerOption[]) => setControllers(data))
      .catch(() => setControllers([]))
  }, [isOpen, editPoint])

  if (!isOpen) return null

  const handleSave = async () => {
    setSaving(true)
    try {
      const token = localStorage.getItem('token')
      if (isEdit) {
        const payload: any = {
          name: name.trim() || editPoint.name,
          measurement_type: measurementType,
          meter_serial: meterSerial.trim() || null,
          controller_id: controllerId ? parseInt(controllerId, 10) : 0, // 0 = odepnij kontroler
        }
        if (controllerId && channel !== '') payload.channel = parseInt(channel, 10)

        const response = await fetch(`/api/measurement-points/${editPoint.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(payload),
        })
        if (response.ok) {
          showSuccess('Zapisano', `Zmiany w punkcie „${name.trim() || editPoint.name}" zostały zapisane`)
          onSave(await response.json().catch(() => ({})))
        } else {
          const err = await response.json().catch(() => ({}))
          showError('Błąd', err.detail || 'Nie udało się zapisać zmian')
        }
        return
      }

      const payload: any = {
        name: name.trim() || null,
        measurement_type: measurementType,
        latitude: coordinates[1].toString(),
        longitude: coordinates[0].toString(),
        meter_serial: meterSerial.trim() || null,
      }
      if (targetSegment?.id) payload.water_segment_id = targetSegment.id
      if (controllerId) payload.controller_id = parseInt(controllerId, 10)
      if (channel !== '') payload.channel = parseInt(channel, 10)

      const response = await fetch('/api/measurement-points/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload),
      })

      if (response.ok) {
        const result = await response.json()
        showSuccess('Sukces', `Punkt pomiarowy „${result.name || name}" został utworzony`)
        onSave(result)
      } else {
        const err = await response.json()
        showError('Błąd', err.detail || 'Nie udało się utworzyć punktu pomiarowego')
      }
    } catch (error) {
      console.error('Error saving measurement point:', error)
      showError('Błąd', 'Wystąpił błąd podczas zapisu punktu pomiarowego')
    } finally {
      setSaving(false)
    }
  }

  const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); handleSave() }
  const handleClose = () => onClose()

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-stretch justify-center z-[9999] sm:items-center sm:p-4">
      <div className="bg-white sm:rounded-xl shadow-xl w-full sm:max-w-md flex flex-col max-h-full sm:max-h-[90vh] overflow-hidden">
        <div className="px-5 sm:px-6 py-4 border-b border-gray-200 shrink-0">
          <h3 className="text-lg font-medium text-gray-900">📊 {isEdit ? 'Edytuj punkt pomiarowy' : 'Dodaj punkt pomiarowy'}</h3>
          <p className="mt-1 text-sm text-gray-500">
            {isEdit
              ? 'Położenie zmienisz przyciskiem „Przenieś" w oknie historii.'
              : (targetSegment?.name ? `Powiązany z odcinkiem: ${targetSegment.name}` : 'Punkt samodzielny (bez odcinka)')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <div className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-6 py-4 space-y-4">
            {/* Współrzędne */}
            <div className="bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-600">
              📍 {coordinates[1].toFixed(6)}, {coordinates[0].toFixed(6)}
            </div>

            {/* Nazwa */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nazwa / ID punktu</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Zostaw puste, aby nadać automatycznie (PP-0001)"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Typ */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Typ punktu pomiarowego</label>
              <select
                value={measurementType}
                onChange={(e) => setMeasurementType(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              >
                {MEASUREMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            {/* Nr wodomierza */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nr seryjny wodomierza (opcjonalnie)</label>
              <input
                type="text"
                value={meterSerial}
                onChange={(e) => setMeterSerial(e.target.value)}
                placeholder="np. FM-SN-00417"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Telemetria: kontroler + kanał */}
            <div className="rounded-lg border border-gray-200 p-3 bg-gray-50/50">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Telemetria (opcjonalnie)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Kontroler</label>
                  <select
                    value={controllerId}
                    onChange={(e) => setControllerId(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-2 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">— brak —</option>
                    {controllers.map(c => <option key={c.id} value={c.id}>{c.identifier}{c.name ? ` (${c.name})` : ''}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Kanał</label>
                  <input
                    type="number"
                    min={0}
                    value={channel}
                    onChange={(e) => setChannel(e.target.value)}
                    placeholder="np. 1"
                    disabled={!controllerId}
                    className="w-full rounded-lg border border-gray-300 px-2 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  />
                </div>
              </div>
              <p className="mt-1 text-[11px] text-gray-400">Kanał = wejście na kontrolerze, do którego podłączony jest ten wodomierz.</p>
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 px-5 sm:px-6 py-4 border-t border-gray-200 shrink-0">
            <button
              type="button"
              onClick={handleClose}
              disabled={saving}
              className="min-h-[44px] px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Anuluj
            </button>
            <button
              type="submit"
              disabled={saving}
              className="min-h-[44px] px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Zapisywanie...' : (isEdit ? 'Zapisz zmiany' : 'Dodaj punkt')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
